import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  signal,
} from '@angular/core';
import { BaseitemsModalComponent } from './baseitems-modal/baseitems-modal.component';
import { Baseitems_SWTYP_ModalComponent } from './baseitems-swtyp-modal/baseitems-swtyp-modal.component';
import { Baseitems_SSCHF_ModalComponent } from './baseitems-sschf-modal/baseitems-sschf-modal.component';
import { Baseitems_SBAGP_ModalComponent } from './baseitems-sbagp-modal/baseitems-sbagp-modal.component';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { Baseitems_SGATT_ModalComponent } from './baseitems-sgatt-modal/baseitems-sgatt-modal.component';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { Baseitems_SBAUW_ModalComponent } from './baseitems-sbauw-modal/baseitems-sbauw-modal.component';
import { DBStatus } from 'src/app/types/api';

@Component({
  selector: 'app-baseitems-wrapper',
  template: `
    @if (tabelle()) {
      @switch (tabelle()) {
        @case ('SBAGP') {
          <app-baseitems-sbagp-modal [(isModalOpen)]="isModalOpen" [(route)]="route" (refresh)="emit($event)" />
        }
        @case ('SSCHF') {
          <app-baseitems-sschf-modal [(isModalOpen)]="isModalOpen" [(route)]="route" (refresh)="emit($event)" />
        }
        @case ('SWTYP') {
          <app-baseitems-swtyp-modal [(isModalOpen)]="isModalOpen" [(route)]="route" (refresh)="emit($event)" />
        }
        @case ('SBAUW') {
          <app-baseitems-sbauw-modal [(isModalOpen)]="isModalOpen" [(route)]="route" (refresh)="emit($event)" />
        }
        @case ('SGATT') {
          <app-baseitems-sgatt-modal
            [(isModalOpen)]="isModalOpen"
            [(route)]="route"
            (refresh)="emit($event)"
            (redirect)="redirectTo($event)" />
        }
        @default {
          <app-baseitems-modal [(isModalOpen)]="isModalOpen" [(route)]="route" (refresh)="emit($event)" />
        }
      }
    }
  `,
  imports: [
    BaseitemsModalComponent,
    Baseitems_SWTYP_ModalComponent,
    Baseitems_SSCHF_ModalComponent,
    Baseitems_SBAGP_ModalComponent,
    Baseitems_SBAUW_ModalComponent,
    Baseitems_SGATT_ModalComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseItemsWrapperComponent implements BaseItemsHandler {
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(MrTranslatePipe);
  readonly refreshList = output<string[]>();
  readonly refresh = output<BaseItem[]>();
  readonly isModalOpen = model<boolean>();
  readonly route = model.required<BaseDataRoute>();

  protected readonly tabelle = computed(() => this.route()?.tabelle);
  private saved_route = signal<BaseDataRoute>(null);

  constructor() {
    effect(() => {
      const tabelle = this.route()?.tabelle.toUpperCase();
      if (!tabelle) return;
      try {
        assertBaseDataTable(tabelle);
        this.route().tabelle = tabelle;
      } catch (err) {
        this.toastr.warning(this.translate.transform('Tabelle ist nicht unterstützt'));
        this.isModalOpen.set(false);
      }
    });

    const overlay = inject(OverlayService);
    effect(() => {
      if (this.tabelle() == 'SBFEK' && this.isModalOpen())
        setTimeout(() => overlay.setOverlay({ overlay: 'gleisCodeVerwaltung', isTopOverlay: true }));
    });

    effect(() => {
      if (this.saved_route() && !this.isModalOpen()) {
        this.isModalOpen.set(true);
        this.route.set(this.saved_route());
        this.saved_route.set(null);
      }
    });
  }

  protected emit(list: BaseItem[]) {
    this.refresh.emit(list);
    this.refreshList.emit(list.map(row => row.Bezeichnung));
  }

  protected redirectTo(route: BaseDataRoute) {
    this.saved_route.set(this.route());
    this.route.set(route);
  }
}
export type BaseDataTable = (typeof BASE_DATA_TABLES)[number];
export const BASE_DATA_TABLES = [
  'KSPEZW',
  'SSCHF',
  'SWTYP',
  'SBFEK',
  'SBAGP',
  'SBEFT',
  'SSCHW',
  'SZUBF',
  'SRADL',
  'SANTR',
  'SVEST',
  'SRICH',
  'SBEFT',
  'SEIND',
  'SUNTG',
  'SGATT',
  'SSTAMM',
  'SMEH',
  'SBAUW',
  'KAUFUS',
  'KEQUI',
  'SPRUE',
  'SMANG',
  'SLAGE',
  'SINST',
] as const;

export interface BaseItem extends Partial<DBStatus> {
  ID: number;
  Bezeichnung: string;
}

export interface BaseItemsHandler {
  refresh: OutputEmitterRef<BaseItem[]>;
  isModalOpen: ModelSignal<boolean>;
  route: ModelSignal<BaseDataRoute>;
}

export interface BaseDataRoute {
  tabelle: BaseDataTable;
  title?: string;
  sub_id?: number;
  selected?: unknown;
}

export function assertBaseDataTable(table: string): asserts table is BaseDataTable {
  if (!BASE_DATA_TABLES.includes(table as BaseDataTable)) throw new Error('basedata-table is invalid');
}
